import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, HelpCircle } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import NotificationCenter from './NotificationCenter';

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // MODIFICATION : Le Bureau d'Étude est maintenant considéré comme un rôle de gestion (Management)
  // Il partage donc le chemin d'aide "admin" avec la direction.
  const isManagementRole = user?.role === 'admin' || user?.role === 'bureau_etude';
  const helpPath = isManagementRole ? '/help/admin' : '/help/employee';

  // Système de notifications (admin + bureau_etude uniquement)
  const {
    groupedNotifications,
    unreadCount,
    permissionGranted,
    requestPermission,
    markAsRead,
    markAllAsReadByType,
    clearRead
  } = useNotifications(user);

  // Afficher le centre de notifications uniquement pour admin et bureau_etude
  const showNotifications = user?.role === 'admin' || user?.role === 'bureau_etude';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc' }}>
      
      {/* HEADER FIXE */}
      <header style={{ 
        height: '70px', 
        background: 'rgba(255,255,255,0.95)', 
        backdropFilter: 'blur(10px)',
        padding: '0 24px', 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)', 
        position: 'sticky', top: 0, zIndex: 100
      }}>
        
        {/* GAUCHE : LOGO CENTRALIS + POUR + LOGO OGER */}
        <div
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 10 }}
        >
          <img src="/logo.svg" alt="Centralis" style={{ height: '30px', objectFit: 'contain' }} />
          <span style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '3px'
          }}>
            pour
          </span>
          <img src="/logo.png" alt="Oger Energies" style={{ height: '40px', objectFit: 'contain' }} />
        </div>
        
        {/* DROITE : USER & ACTIONS */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', zIndex: 10 }}>
          
          <div className="user-details" style={{ textAlign: 'right' }}> 
            <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1e293b' }}>{user?.full_name}</div>
            <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform:'uppercase', fontWeight:'600' }}>
              {user?.role?.replace('_', ' ')}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* Centre de notifications */}
            {showNotifications && (
              <NotificationCenter
                groupedNotifications={groupedNotifications}
                unreadCount={unreadCount}
                permissionGranted={permissionGranted}
                onRequestPermission={requestPermission}
                onMarkAsRead={markAsRead}
                onMarkAllAsReadByType={markAllAsReadByType}
                onClearRead={clearRead}
              />
            )}

            <button
              onClick={() => navigate(helpPath)}
              title="Aide" 
              style={{ 
                background: '#f1f5f9', border: 'none', color: '#64748b', 
                width: '40px', height: '40px', borderRadius: '12px', 
                cursor: 'pointer', display:'flex', alignItems:'center', justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => {e.currentTarget.style.background = '#e2e8f0';}}
              onMouseLeave={e => {e.currentTarget.style.background = '#f1f5f9';}}
            >
              <HelpCircle size={20} />
            </button>

            <button 
              onClick={logout} 
              title="Déconnexion" 
              style={{ 
                background: '#fee2e2', border: 'none', color: '#ef4444', 
                width: '40px', height: '40px', borderRadius: '12px', 
                cursor: 'pointer', display:'flex', alignItems:'center', justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => {e.currentTarget.style.background = '#fecaca';}}
              onMouseLeave={e => {e.currentTarget.style.background = '#fee2e2';}}
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* CONTENU PRINCIPAL */}
      <main className="container" style={{ flex: 1, paddingBottom: '40px', maxWidth: '1200px', margin: '0 auto', width: '100%', padding: '24px' }}>
        {children}
      </main>

      {/* STYLES LOCAUX */}
      <style>{`
        .user-details {
          display: none;
        }
        @media (min-width: 640px) {
          .user-details {
            display: block;
          }
        }
      `}</style>

    </div>
  );
}